// Delegate — the resolve ceremony moved to `@heidi/agents/data`
// (ARCFE-1079 · D4). This file keeps its name and its signature, so both call
// sites in `InlineClarificationBlock` and any `jest.mock` literal on the door
// resolve unchanged.
//
// THIS WAS THE ONE MODULE THE `tool-approvals` ROOT STILL OWED, and it owed it
// for one reason: THE WORDS. The package hook is headless — it fires an
// `onResolveError` port exactly where `toast(...)` fired, and the copy, with
// the same two `intl.formatMessage` ids, stays with it. That is the ratified
// port mechanism, and the port is OPTIONAL in the package signature, so
// pointing `InlineClarificationBlock` straight at `useResolveApproval` from
// `@heidi/agents/data` would delete the toast without failing anything that
// throws — `toast` is simply never called.
//
// WHAT MOVED IT is that the constraint is not "the copy lives
// app-side" but "the copy lives where `@heidi/design-system` may be imported".
// `/ui` is such a zone; `/data` is not. Body unchanged, both ids verbatim.
//
// THE INTERCEPTION POINT IS THE DOOR. This used to read
// `resolveToolApproval` from `@/features/tool-approvals/api` so that four
// consumer suites' `jest.mock('@/features/tool-approvals/api')` literals kept
// intercepting. That alias is retired; those suites now name
// `@heidi/agents/data` — spreading `jest.requireActual` first, because the door
// is a barrel and a bare factory blanks it — and the relative `../../data`
// below is the module that door re-exports, so one registry entry serves both.
//
// Everything else (the synchronous verdict latch, the unaccepted-response
// unlock, the inbox invalidation) is data behaviour and went with the hook.
import { toast } from '@heidi/design-system/toast';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { PendingToolApproval } from '../../contracts';
import {
  resolveToolApproval,
  useResolveApproval as useResolveApprovalCore,
  type ApprovalVerdict,
} from '../../data';

export type { ApprovalVerdict };

/**
 * NARROWER THAN `/data`'s SAME-NAMED TYPE, and not merged with it: the core
 * hook's `UseResolveApprovalOptions` also requires `resolveApproval`, which is
 * exactly the member this wrapper supplies so its callers never have to.
 */
export interface UseResolveApprovalOptions {
  /** Prefix for resolve-failure logs (e.g. ``[inline-clarification]``). */
  logTag?: string;
}

export function useResolveApproval(
  approval: Pick<PendingToolApproval, 'agentRunId' | 'approvalId'>,
  options?: UseResolveApprovalOptions
) {
  const intl = useIntl();

  // Memoised so the port's identity is stable across renders — the package
  // hook lists it in `resolve`'s dependency array, and an inline closure would
  // hand every render a new `resolve`.
  const onResolveError = useCallback(() => {
    toast({
      tone: 'destructive',
      title: intl.formatMessage({
        id: 'Wtt3jet+2S',
        defaultMessage: 'Could not send your answer',
      }),
      description: intl.formatMessage({
        id: 'VjB59sDdPt',
        defaultMessage: 'Please try again.',
      }),
    });
  }, [intl]);

  return useResolveApprovalCore(approval, {
    resolveApproval: resolveToolApproval,
    logTag: options?.logTag,
    onResolveError,
  });
}
